import { formatCompact, formatInteger } from "@/shared/utils/format";

import { ChartLegend, useChartTip, useChartWidth } from "./chart-kit";
import { EmptyState } from "./EmptyState";

export type LineSeries = { key: string; label: string; tone: string; values: number[] };

const HEIGHT = 230;
const PAD = { top: 12, right: 10, bottom: 28, left: 44 };

/**
 * Várias linhas da MESMA grandeza no mesmo eixo (menções de imprensa e as
 * do porta-voz escolhido, no v1 "Exposição Diária") — e só assim: duas
 * grandezas diferentes vão em gráficos diferentes. `formatValue` escreve
 * o eixo e a dica.
 */
export function MultiLineChart({ labels, series, formatValue = formatInteger, formatAxis = formatCompact, area = false }: {
  labels: string[];
  series: LineSeries[];
  formatValue?: (value: number) => string;
  formatAxis?: (value: number) => string;
  /** Área esmaecida sob a linha — só com uma série. */
  area?: boolean;
}) {
  const [frame, width] = useChartWidth<HTMLDivElement>();
  const { bind, tip } = useChartTip(frame);
  const max = Math.max(...series.flatMap(line => line.values), 0);
  if (!labels.length || !max) return <EmptyState/>;
  const plotWidth = Math.max(80, width - PAD.left - PAD.right);
  const plotHeight = HEIGHT - PAD.top - PAD.bottom;
  const x = (index: number) => PAD.left + (labels.length === 1 ? plotWidth / 2 : (index * plotWidth) / (labels.length - 1));
  const y = (value: number) => PAD.top + plotHeight * (1 - value / max);
  const slot = plotWidth / Math.max(labels.length, 1);
  const every = Math.max(1, Math.ceil(labels.length / Math.max(1, Math.floor(plotWidth / 64))));
  return (
    <div className="chart-frame" ref={frame}>
      <svg data-export width={width} height={HEIGHT} viewBox={`0 0 ${width} ${HEIGHT}`} role="img" aria-label={series.map(line => line.label).join(" e ")}>
        {[0, 0.5, 1].map(step => (
          <g key={step}>
            <line x1={PAD.left} x2={width - PAD.right} y1={y(max * step)} y2={y(max * step)} className="chart-grid-line"/>
            <text x={PAD.left - 6} y={y(max * step)} className="chart-axis-text" textAnchor="end" dominantBaseline="middle">{formatAxis(max * step)}</text>
          </g>
        ))}
        {area && series.length === 1 && labels.length > 1 && (
          <path
            d={`${series[0].values.map((value, index) => `${index ? "L" : "M"}${x(index).toFixed(1)},${y(value).toFixed(1)}`).join(" ")} L${x(labels.length - 1).toFixed(1)},${y(0).toFixed(1)} L${x(0).toFixed(1)},${y(0).toFixed(1)} Z`}
            className={`chart-area chart-tone--${series[0].tone}`}
          />
        )}
        {series.map(line => (
          <path
            key={line.key}
            d={line.values.map((value, index) => `${index ? "L" : "M"}${x(index).toFixed(1)},${y(value).toFixed(1)}`).join(" ")}
            className={`chart-line chart-stroke--${line.tone}`}
          />
        ))}
        {labels.length <= 60 && series.map(line => line.values.map((value, index) => (
          <circle key={`${line.key}-${index}`} cx={x(index)} cy={y(value)} r={4} className={`chart-dot chart-stroke--${line.tone}`}/>
        )))}
        {labels.map((label, index) => (
          <g key={label + index}>
            <rect
              x={x(index) - slot / 2}
              y={PAD.top}
              width={slot}
              height={plotHeight}
              className="chart-row-hit"
              tabIndex={0}
              aria-label={`${label}: ${series.map(line => `${line.label} ${formatValue(line.values[index] ?? 0)}`).join(", ")}`}
              {...bind({ title: label, lines: series.map(line => ({ label: line.label, tone: line.tone, value: formatValue(line.values[index] ?? 0) })) })}
            />
            {index % every === 0 && <text x={x(index)} y={HEIGHT - 8} className="chart-axis-text" textAnchor="middle">{label}</text>}
          </g>
        ))}
      </svg>
      {tip}
      {series.length > 1 && <ChartLegend items={series}/>}
    </div>
  );
}
